'use strict';

const ROLES = Object.freeze({
  SUPERADMIN: 'superadmin',
  ADMIN: 'admin',
  SUPERUSER: 'superuser',
  USER: 'user',
  EXTERNALUSER: 'externaluser',
  REPORTUSER: 'reportuser',
  CAMERAUSER: 'camerauser',
});

const ROLES_PRIORITY = Object.freeze({
  superadmin: 7,
  admin: 6,
  superuser: 5,
  user: 4,
  externaluser: 3,
  reportuser: 2,
  camerauser: 1,
});

const ROLES_NAMES = Object.freeze({
  superadmin: 'Super Admin',
  admin: 'Admin',
  superuser: 'Super User',
  user: 'User',
  externaluser: 'External User',
  reportuser: 'Report user',
  camerauser: 'Camera user',
});

// Früh (Early)
// Spät (Late)
// Nacht (Night)
// off shift - frei
const SHIFTS = Object.freeze({
  EARLY: 'early',
  LATE: 'late',
  NIGHT: 'night',
  OFF_SHIFT: 'off shift',
});

const SHIFT_TYPES = Object.freeze({
  UPSTREAM_PLANT: 'Upstream Plant',
  POWER_PLANT: 'Power Plant',
});

const ABSENCE_TYPES = Object.freeze({
  WORKING_HOURS: 'WORKING_HOURS',
  HOLIDAY: 'HOLIDAY',
  WORKING_HOURS_REDUCTION_DAY: 'WORKING_HOURS_REDUCTION_DAY',
  ILLNESS: 'ILLNESS',
  TRAINING: 'TRAINING',
});

const ABSENCE_IS_NOT_PRESENT = Object.freeze([
  ABSENCE_TYPES.ILLNESS,
  ABSENCE_TYPES.HOLIDAY,
  ABSENCE_TYPES.TRAINING,
  ABSENCE_TYPES.WORKING_HOURS_REDUCTION_DAY,
]);

const USER_ACTIVITY_BASE_TYPES = Object.freeze({
  CREATE: 'CREATE',
  CREATE_AUTOMATICALLY: 'CREATE_AUTOMATICALLY',
  UPDATE: 'UPDATE',
  UPDATE_PASSWORD: 'UPDATE_PASSWORD',
  DELETE: 'DELETE',
});

const USER_ACTIVITY_BASE_ENTITY = Object.freeze({
  COMPANY: 'COMPANY',
  CONTACT_DETAILS: 'CONTACT_DETAILS',
  CORPORATION: 'CORPORATION',
  OPERATIONAL_AREA: 'OPERATIONAL_AREA',
  SHIFT_DAY: 'SHIFT_DAY',
  PLANNED_VALUES: 'PLANNED_VALUES',
  FABRIC_DATA: 'FABRIC_DATA',
  SHIFT_POSITION: 'SHIFT_POSITION',
  METRICS: 'METRICS',
  CALENDAR: 'CALENDAR',
  CALENDAR_DAY: 'CALENDAR_DAY',
  LOG_BOOK: 'LOG_BOOK',
  LOG_BOOK_DAY: 'LOG_BOOK_DAY',
  SHIFT_TEMPLATE: 'SHIFT_TEMPLATE',
  SUPPLIER: 'SUPPLIER',
  TEAM: 'TEAM',
  USER: 'USER',
  SETTINGS: 'SETTINGS',
});

const CAN_DELETE = Object.freeze([ROLES.SUPERADMIN, ROLES.ADMIN]);

const COMMON_ADMIN = Object.freeze([ROLES.SUPERADMIN, ROLES.ADMIN]);
const AVG_ADMIN = Object.freeze([ROLES.SUPERADMIN, ROLES.ADMIN]);
const AVG_USER = Object.freeze([ROLES.SUPERUSER, ROLES.USER, ROLES.REPORTUSER]);

const LICENSE_MODULES = Object.freeze({
  RPTRADE: 'RP Trade',
  AVG: 'Sensis AVG BTB',
  SALES: 'Sales',
  EKAV: 'eKAV',
  DIGITAL35: 'Digital 35',
  REPORTS: 'Reports',
  IS_REPORTS_PART: 'IS_REPORTS_PART',
  ONE: 'One',
  AUDIT: 'Audit',
  DAILY: 'Daily',
  II: 'II',
  TICKETS: 'Tickets',
  VIEW: 'View',
  GMVA: 'GMVA BTB',
});
const ACCESS_TO_REPORTS = Object.freeze([LICENSE_MODULES.REPORTS, LICENSE_MODULES.EKAV, LICENSE_MODULES.DIGITAL35]);

const ENTRY_TYPES = Object.freeze({
  TEXT: 'text',
  MULTILINE_TEXT: 'multiline_text',
  INT: 'int',
  DECIMAL: 'decimal',
  RADIO_BUTTON: 'radio_button',
  CHECKBOX: 'checkbox',
  MULTIPLE_CHECKBOXES: 'multiple_checkboxes',
  DROPDOWN: 'dropdown',
  PHOTO: 'photo',
  FILE: 'file',
  FEED: 'feed',
  DRAWING: 'drawing',
  SIGNATURE: 'signature',
  LOCATION: 'location',
  DATE: 'date',
  DATE_TIME: 'date_time',
  TIME: 'time',
  PICTURE_IN_TEMPLATE: 'picture_in_template',
  PDF_ATTACHMENT: 'pdf_attachment',
  TEXT_BLOCK: 'text_block',
  LIST: 'list',
  FORMULA: 'formula',
  QR_CODE: 'qr_code',
  BAR_CODE: 'bar_code',
  PAGE_BREAK: 'page_break',
  EMPTY_SPACE: 'empty_space',
});

const TICKET_TEMPLATE_DETAILS_REQUIRED_FIELDS = {
  email: false,
  emailCC: false,
  middlewareId: true,
  defaultTeam: false,
  defaultUser: false,
};

const REPORT_TEMPLATE_DETAILS_REQUIRED_FIELDS = {
  email: false,
  emailCC: false,
  checklistNo: false,
  teams: true,
};

const ONE_TEMPLATE_DETAILS_REQUIRED_FIELDS = {
  email: false,
  emailCC: false,
  checklistNo: false,
};

const ENTRY_TYPES_DYNAMIC_SIZE_IN_PDF = [
  ENTRY_TYPES.TEXT,
  ENTRY_TYPES.MULTILINE_TEXT,
  ENTRY_TYPES.DROPDOWN,
  ENTRY_TYPES.DRAWING,
  ENTRY_TYPES.TEXT_BLOCK,
  ENTRY_TYPES.LIST,
  ENTRY_TYPES.INT,
  ENTRY_TYPES.DECIMAL,
  ENTRY_TYPES.CHECKBOX,
  ENTRY_TYPES.PHOTO,
  ENTRY_TYPES.PICTURE_IN_TEMPLATE,
  ENTRY_TYPES.FILE,
  ENTRY_TYPES.SIGNATURE,
  ENTRY_TYPES.LOCATION,
  ENTRY_TYPES.DATE,
  ENTRY_TYPES.DATE_TIME,
  ENTRY_TYPES.TIME,
  ENTRY_TYPES.FORMULA,
  ENTRY_TYPES.BAR_CODE,
  ENTRY_TYPES.RADIO_BUTTON,
  ENTRY_TYPES.MULTIPLE_CHECKBOXES,
  ENTRY_TYPES.QR_CODE,
  ENTRY_TYPES.EMPTY_SPACE,
];

const ENTRY_TYPES_FILES_BELONG_MILESTONE = [ENTRY_TYPES.FILE, ENTRY_TYPES.PHOTO, ENTRY_TYPES.SIGNATURE, ENTRY_TYPES.DRAWING];

const ENTRY_TYPES_FILES_BELONG_TICKET = [...ENTRY_TYPES_FILES_BELONG_MILESTONE];

const ENTRY_TYPES_FILES = [
  ENTRY_TYPES.FILE,
  ENTRY_TYPES.PHOTO,
  ENTRY_TYPES.SIGNATURE,
  ENTRY_TYPES.DRAWING,
  ENTRY_TYPES.PICTURE_IN_TEMPLATE,
  ENTRY_TYPES.PDF_ATTACHMENT,
];

const ENTRY_TYPES_TEMPLATE_FILES = [ENTRY_TYPES.PICTURE_IN_TEMPLATE, ENTRY_TYPES.PDF_ATTACHMENT];

const ENTRY_TYPES_DRAW = [ENTRY_TYPES.SIGNATURE, ENTRY_TYPES.DRAWING];

const ENTRY_TYPE_VALUES = Object.freeze({
  text: 'TEXT',
  multiline_text: 'MULTILINE_TEXT',
  int: 'INT',
  decimal: 'DECIMAL',
  file: 'FILE',
  photo: 'PHOTO',
  feed: 'FEED',
  signature: 'SIGNATURE',
  drawing: 'DRAWING',
  checkbox: 'CHECKBOX',
  radio_button: 'RADIO_BUTTON',
  multiple_checkboxes: 'MULTIPLE_CHECKBOXES',
  location: 'LOCATION',
  date: 'DATE',
  date_time: 'DATE_TIME',
  dropdown: 'DROPDOWN',
  formula: 'FORMULA',
  time: 'TIME',
  text_block: 'TEXT_BLOCK',
  list: 'LIST',
  picture_in_template: 'PICTURE_IN_TEMPLATE',
  pdf_attachment: 'PDF_ATTACHMENT',
  qr_code: 'QR_CODE',
  bar_code: 'BAR_CODE',
});

const DATA_TYPES = Object.freeze({
  STRING: 'string',
  INT: 'int',
  FLOAT: 'float',
});

const TRANSLATE_LANGUAGE = Object.freeze({
  Swedish: 'SV',
  Slovenian: 'SL',
  Slovak: 'SK',
  Romanian: 'RO',
  Latvian: 'LV',
  Lituanien: 'LT',
  Hungarian: 'HU',
  Finnish: 'FI',
  Estonian: 'ET',
  Greek: 'EL',
  Danish: 'DA',
  Czech: 'CS',
  Bulgarian: 'BG',
  English: 'EN',
  German: 'DE',
  Japanese: 'JA',
  French: 'FR',
  Italian: 'IT',
  Spanish: 'ES',
  Dutch: 'NL',
  Polish: 'PL',
  Portuguese: 'PT',
  Chinese: 'ZH',
});

const SPEECH_LANGUAGE = Object.freeze({
  Swedish: 'sv',
  Slovenian: 'sl',
  Slovak: 'sk',
  Romanian: 'ro',
  Latvian: 'lv',
  Hungarian: 'hu',
  Finnish: 'fi',
  Estonian: 'et',
  Greek: 'el',
  Danish: 'da',
  Czech: 'cs',
  Bulgarian: 'bg',
  English: 'en',
  German: 'de',
  Japanese: 'ja',
  French: 'fr',
  Italian: 'it',
  Spanish: 'es',
  Dutch: 'nl',
  Polish: 'pl',
  Portuguese: 'pt',
  Ukrainian: 'uk',
  Chinese: 'zh',
});

const REPORT_STATUSES = Object.freeze({
  IN_PROGRESS: 'inprogress',
  COMPLETED: 'completed',
});

const fieldNameRegex = '^[a-zA-Z0-9_]+$';

const VALIDATION_RULES = Object.freeze({
  REQUIRED: 'required',
  IS_ATTACH_FILE_TO_EMAIL: 'isAttachFileToEmail',
  MIN: 'min',
  MAX: 'max',
  LIMIT: 'limit',
  LENGTH: 'length',
  MIN_LENGTH: 'minLength',
  SIZE_IN_PDF: 'size_in_pdf',
  AUTOMATICALLY_SELECTED: 'automatically_selected',
  REQUIRED_WHEN: 'requiredWhen',
});

const ENTRY_TYPES_VALIDATION_RULES = Object.freeze({
  [ENTRY_TYPES.TEXT]: [VALIDATION_RULES.REQUIRED],
  [ENTRY_TYPES.MULTILINE_TEXT]: [VALIDATION_RULES.REQUIRED],
  [ENTRY_TYPES.QR_CODE]: [VALIDATION_RULES.REQUIRED],
  [ENTRY_TYPES.BAR_CODE]: [VALIDATION_RULES.REQUIRED, VALIDATION_RULES.MIN_LENGTH, VALIDATION_RULES.LENGTH],
  [ENTRY_TYPES.FILE]: [VALIDATION_RULES.REQUIRED],
  [ENTRY_TYPES.PHOTO]: [VALIDATION_RULES.REQUIRED],
  [ENTRY_TYPES.SIGNATURE]: [VALIDATION_RULES.REQUIRED],
  [ENTRY_TYPES.INT]: [VALIDATION_RULES.REQUIRED, VALIDATION_RULES.MIN, VALIDATION_RULES.MAX],
  [ENTRY_TYPES.DECIMAL]: [VALIDATION_RULES.REQUIRED, VALIDATION_RULES.MIN, VALIDATION_RULES.MAX],
  [ENTRY_TYPES.LIST]: [VALIDATION_RULES.LIMIT],
});

const VALIDATION_RULE_VALUE_TYPE = Object.freeze({
  [VALIDATION_RULES.MIN]: 'number',
  [VALIDATION_RULES.MAX]: 'number',
  [VALIDATION_RULES.MIN_LENGTH]: 'number',
  [VALIDATION_RULES.LENGTH]: 'number',
  [VALIDATION_RULES.SIZE_IN_PDF]: 'number',
});

const DEFAULT_ERROR_MESSAGE = 'internal_server_error';
const DEFAULT_ERROR_TITLE = 'default_error_title';
const SORT_ASC_TITLE = 'sort_asc_title';
const SORT_DESC_TITLE = 'sort_desc_title';
const NO_TABLE_DATA_TITLE = 'no_table_data';

const ORDER_OFFER_TYPES = Object.freeze([
  { name: 'Autfrag', value: 0 },
  { name: 'Angebot', value: 1 },
  { name: 'Musterauftrag', value: 2 },
  { name: 'Rückerstattung', value: 200 },
  { name: 'Bericht', value: 900 },
  { name: 'Fahrtbericht', value: 901 },
]);

const SUPPORTED_LANGUAGES = Object.freeze(['de', 'en']);

const SUPPORTED_LOCALES = Object.freeze({
  de: 'de-DE',
  en: 'en-EN',
});

const DEFAULT_LANGUAGE = 'de';

const LANGUAGE_TITLES = Object.freeze({
  de: 'Deutsch',
  en: 'English',
});

const ENCODING_OPTIONS = Object.freeze([
  { value: 'utf8', text: 'utf8' },
  { value: 'latin1', text: 'latin1' },
]);

const PDF_BACKGROUND_TYPE = Object.freeze({
  FIRST_PAGE: 1,
  FOLLOWING_PAGES: 2,
});

const SHIFT_LOG_STATUSES = Object.freeze({
  IN_PROGRESS: 'inprogress',
  FINISHED: 'finished',
});

const CASE_STATUSES = Object.freeze({
  ACTIVE: 'active',
  IN_PROGRESS: 'inprogress',
  FINISHED: 'finished',
});

const TICKET_STATUSES = Object.freeze({
  NEW: 'new',
  IN_PROGRESS: 'inprogress',
  FINISHED: 'finished',
});

const PROJECT_STATUSES = Object.freeze({
  ACTIVE: 'active',
  // IN_PROGRESS: 'inprogress',
  FINISHED: 'finished',
});

const PROJECT_PDF_TYPES = Object.freeze({
  DEFAULT: 'Default',
  EGGERSMANN: 'Eggersmann',
});

const STATUS_CODES = Object.freeze({
  OK: 200,
  CREATED: 201,
  ACCEPTED: 202,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
});

const ERROR_CODES = Object.freeze({
  FORBIDDEN: 'forbidden',
  NOT_FOUND: 'not_found',
  BAD_REQUEST: 'bad_request',
  DEVICES_LIMIT_REACHED: 'devices_limit_reached',
  VALIDATION_FAILED: 'validation_failed',
  INTERNAL_SERVER_ERROR: 'internal_server_error',
  UNAUTHORIZED: 'unauthorized',
  EMAIL_TAKEN: 'email_taken',
  USERNAME_TAKEN: 'username_taken',
  IMPORT_IN_PROGRESS: 'import_in_progress',
  AUTH_TOKEN_EXPIRED: 'auth_token_expired',
  AUTH_TOKEN_INVALID_ISSUER: 'auth_token_invalid_issuer',
});

const RESPONSE_MESSAGES = Object.freeze({
  NOT_FOUND: 'Not Found',
  FORBIDDEN: `This action isn't available for your role`,
  FILE_NOT_FOUND: 'File was not found',
  BAD_REQUEST: 'Bad Request',
  DEFAULT_ERROR_MESSAGE: 'Internal Server Error.',
  VALIDATION_FAILED: 'Validation failed.',
  UNAUTHORIZED: 'Unauthorized',
});

const RESOURCES = Object.freeze({
  ORDERS: 'orders',
  SETTINGS: 'settings',
  DEVICES: 'devices',
  ARTICLES: 'articles',
  CUSTOMERS: 'customers',
  LICENSE: 'license',
  USERS: 'users',
  CATALOGUES: 'catalogues',
  PRICE_LIST: 'pricelist',
  IMPORT_REQUESTS: 'import_requests',
  DASHBOARD: 'dashboard',
  STOCKS: 'stocks',
  EXPORT: 'export',
});

const RESOURCE_ACTIONS = Object.freeze({
  ALL: '*',
  READ: 'read',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  EMAIL: 'email',
});

const PDF_PRESETS = Object.freeze({
  ORDERS: 1,
  REPORTS: 2,
  STATISTICS: 3,
});

const DB_NAMING = {
  id: '_id',
  customerNumber: 'Konto',
  status: 'Status',
  name1: 'Name1',
  name2: 'Name2',
  name3: 'Name3',
  street: 'Str',
  zip: 'Plz',
  city: 'Ort',
  state: 'Staat',
  email: 'Email',
  tel: 'Tel',
  fax: 'Fax',
  webAddress: 'WebAdresse',
  representativeNumber: 'VertreterNummer',
  deliveryInformation1: 'Lieferhinweis1',
  deliveryInformation2: 'Lieferhinweis2',
  customerGroup: 'Kundengruppe',
  customerGroupName: 'KundengruppeBezeichnung',
  salesRepresentative: 'MitarbeiterIdAd',
  evkPriceList: 'EvkPreisListe',
  matchcode: 'Matchcode',
  salesTerritory: 'Verkaufsgebiet',
  assessment: 'Beurteilung',
  currency: 'Waehrung',
  accountConditions: 'Konditionskonto',
  company: 'firma',
  timestampUpdate: 'ZeitStempelUpdate',
  year: 'Jahr',
  period: 'Periode',
  periodDescription: 'PeriodenBezeichnung',
  sales: 'Umsatz',
  primaryKey: 'Primaerschluessel',
  articleId: 'ArtikelId',
  keywordId: 'SchlagwortId',
  accountOrGroup: 'Konto_oder_Gruppe',
  priceGroup: 'Preisgruppe',
  priceIndicator: 'PreisKennzeichen',
  dateFrom: 'Datum_Von',
  dateTo: 'Datum_Bis',
  priceList: 'Preisliste',
  priceArticleLevel: 'PreisArtikelStufe',
  priceArticleId: 'PreisArtikelId',
  descriptionId: 'BezId',
  language: 'Sprache',
  description: 'Bezeichnung',
  table: 'Tabelle',
  shortDescription: 'KurzBezeichnung',
  longDescription: 'LangBezeichnung',
  imageRanking: 'Bildrangfolge',
  imageNumber: 'Bildnummer',
  articlePositionInPicture: 'ArtikelPositionImBild',
  displayImagePath: 'AnzeigeBildPfad',
  dataHash: 'DataHash',
  keyword: 'Schlagwort',
  subKeywordId: 'SubSchlagwortId',
  alternativeArticleId: 'AlternativeArtikelId',
  type: 'Typ',
  info: 'Info',
  stock: 'Lager',
  availabilityDate: 'Verfuegbarkeitsdatum',
  unitsInStock: 'Lagerbestand',
  unitsOrdered: 'Bestellbestand',
  unitsReserved: 'Reservierungen',
  groupKz: 'GruppenKz',
  group: 'Gruppe',
  fromQuantity13WithoutPrice: 'AbMenge_13_ohne_Preis',
  toDiscount13WithoutPrice: 'ZuAbSchlag_13_ohne_ZuAb',
  zuAbArt: 'ZuAbArt',
  mengenStaffelArt: 'MengenStaffelArt',
};
// Set appointment from 1 to
Array.from({ length: 5 }, (_, i) => {
  DB_NAMING[`appointment${i + 1}`] = `Termin_${i + 1}`;
  DB_NAMING[`appointmentString${i + 1}`] = `Termin_${i + 1}String`;
  DB_NAMING[`appointmentQuality${i + 1}`] = `TerminQualitaet_${i + 1}`;
  DB_NAMING[`availableQuantity${i + 1}`] = `VerfuegbareMenge_${i + 1}`;
  DB_NAMING[`availableQuantitySalesUnit${i + 1}`] = `VerfuegbareMengeVKEinheit_${i + 1}`;
});
// Set sortingVariant from 1 to 5
Array.from({ length: 5 }, (_, i) => (DB_NAMING[`sortingVariant${i + 1}`] = `Sortiervariante_${i + 1}`));
// Set fromQuantity from 1 to 12
Array.from({ length: 12 }, (_, i) => (DB_NAMING[`fromQuantity${i + 1}`] = `AbMenge_${i + 1}`));
// Set toDiscount from 1 to 12
Array.from({ length: 12 }, (_, i) => (DB_NAMING[`toDiscount${i + 1}`] = `ZuAbSchlag_${i + 1}`));
// Set evaluation from 1 to 10
Array.from({ length: 10 }, (_, i) => (DB_NAMING[`evaluation${i + 1}`] = `Auswertung${i + 1}`));

const WS_TYPES = Object.freeze({
  PROJECT: 'PROJECT',
  PROJECT_USER_ALIVE: 'PROJECT_USER_ALIVE',
  PROJECT_TEXT_INPUT: 'PROJECT_TEXT_INPUT',
  PROJECT_CONFIRMATION: 'PROJECT_CONFIRMATION',
  PROJECT_EXCLUDE_FROM_PDF: 'PROJECT_EXCLUDE_FROM_PDF',
  PROJECT_FEED: 'PROJECT_FEED',
  REQUEST_AUTHORIZATION: 'REQUEST_AUTHORIZATION',
  USER_EXIST: 'USER_EXIST',
  HIDE_AUTHORIZATION: 'HIDE_AUTHORIZATION',
  ACCEPT_AUTHORIZATION: 'ACCEPT_AUTHORIZATION',
  DECLINE_AUTHORIZATION: 'DECLINE_AUTHORIZATION',
  ACCEPT_MEETING_AUTHORIZATION: 'ACCEPT_MEETING_AUTHORIZATION',
  DECLINE_MEETING_AUTHORIZATION: 'DECLINE_MEETING_AUTHORIZATION',
  REQUEST_MEETING_HASH: 'REQUEST_MEETING_HASH',
  WAITING_ACCESS: 'WAITING_ACCESS',
});

const API_ACCESS_LEVEL = Object.freeze({
  TEAMS: 'TEAMS',
  LIST: 'LIST',
  FORM_TEMPLATE: 'FORM_TEMPLATE',
  CASE: 'CASE',
  REPORT: 'REPORT',
  FEED: 'FEED',
  USER: 'USER',
  CONTENT: 'CONTENT',
});

const API_ACCESS_PERMISION = Object.freeze({
  READ: 'READ',
  WRITE: 'WRITE',
  DELETE: 'DELETE',
});

const DEFAULT_MASS_CORRECTION_FACTOR = 1.05;

const PDF_CONTENT_TYPE = Object.freeze({
  HEADER: 'HEADER',
  LOGO: 'LOGO',
});

const NO_EDIT_BY_MOBILE = Object.freeze([ENTRY_TYPES.FEED, ENTRY_TYPES.SIGNATURE, ENTRY_TYPES.PAGE_BREAK, ENTRY_TYPES.EMPTY_SPACE]);

const TYPES_LIST_WITHOUT_CHECKBOX = Object.freeze([ENTRY_TYPES.PAGE_BREAK, ENTRY_TYPES.EMPTY_SPACE]);

const TYPES_LIST_THAT_COMPARE_WITH_OTHER = [ENTRY_TYPES.TEXT, ENTRY_TYPES.INT, ENTRY_TYPES.DECIMAL, ENTRY_TYPES.BAR_CODE, ENTRY_TYPES.QR_CODE];

const TYPES_OF_COMPARE = ['equal', 'moreOrEqual', 'lessOrEqual', 'more', 'less'];

const EMPTY_FIELD = 'not equals';
const FILLED_FIELD = 'equals';
const TYPES_OF_FILLING_THE_FIELD = [EMPTY_FIELD, FILLED_FIELD];

const DOCX_CONVERT_ERROR = 'DOCX_CONVERT_ERROR';

const SIZE_IN_PDF_50 = 2;
const SIZE_IN_PDF_100 = 1;

const SIZE_IN_PDF = Object.freeze({
  [ENTRY_TYPES.SIGNATURE]: SIZE_IN_PDF_50,
  [ENTRY_TYPES.FEED]: SIZE_IN_PDF_100,
  [ENTRY_TYPES.PHOTO]: SIZE_IN_PDF_50,
  [ENTRY_TYPES.DRAWING]: SIZE_IN_PDF_50,
  [ENTRY_TYPES.PICTURE_IN_TEMPLATE]: SIZE_IN_PDF_50,
  [ENTRY_TYPES.FILE]: SIZE_IN_PDF_100,
  [ENTRY_TYPES.RADIO_BUTTON]: SIZE_IN_PDF_50,
  [ENTRY_TYPES.MULTIPLE_CHECKBOXES]: SIZE_IN_PDF_100,
  [ENTRY_TYPES.CHECKBOX]: SIZE_IN_PDF_50,
  [ENTRY_TYPES.LOCATION]: SIZE_IN_PDF_50,
  [ENTRY_TYPES.DATE_TIME]: SIZE_IN_PDF_50,
  [ENTRY_TYPES.TIME]: SIZE_IN_PDF_50,
  [ENTRY_TYPES.LIST]: SIZE_IN_PDF_100,
  [ENTRY_TYPES.TEXT_BLOCK]: SIZE_IN_PDF_100,
  [ENTRY_TYPES.FORMULA]: SIZE_IN_PDF_50,
  [ENTRY_TYPES.MULTILINE_TEXT]: SIZE_IN_PDF_100,
  [ENTRY_TYPES.QR_CODE]: SIZE_IN_PDF_100,
  [ENTRY_TYPES.TEXT]: SIZE_IN_PDF_50,
  [ENTRY_TYPES.INT]: SIZE_IN_PDF_50,
  [ENTRY_TYPES.DECIMAL]: SIZE_IN_PDF_50,
  [ENTRY_TYPES.BAR_CODE]: SIZE_IN_PDF_50,
  [ENTRY_TYPES.DROPDOWN]: SIZE_IN_PDF_50,
  [ENTRY_TYPES.DATE]: SIZE_IN_PDF_50,
  [ENTRY_TYPES.PDF_ATTACHMENT]: SIZE_IN_PDF_50,
  [ENTRY_TYPES.EMPTY_SPACE]: SIZE_IN_PDF_50,
});

const STICKER_TYPES = Object.freeze({
  TEXT: 'text',
  BARCODE: 'barcode',
});

const DYNAMIC_DATA_TYPES = Object.freeze({
  STRING: 'String',
  NUMBER: 'Number',
  BOOLEAN: 'Boolean',
});

const VIEW_TYPES = Object.freeze({
  FILE: 'file',
  FOLDER: 'folder',
});

module.exports = {
  VIEW_TYPES,
  PDF_CONTENT_TYPE,
  API_ACCESS_LEVEL,
  API_ACCESS_PERMISION,
  WS_TYPES,
  TRANSLATE_LANGUAGE,
  fieldNameRegex,
  DATA_TYPES,
  ENTRY_TYPES,
  ENTRY_TYPE_VALUES,
  VALIDATION_RULES,
  ENTRY_TYPES_VALIDATION_RULES,
  VALIDATION_RULE_VALUE_TYPE,
  ROLES,
  CAN_DELETE,
  LICENSE_MODULES,
  AVG_ADMIN,
  SHIFTS,
  SHIFT_TYPES,
  ABSENCE_TYPES,
  ABSENCE_IS_NOT_PRESENT,
  AVG_USER,
  COMMON_ADMIN,
  ACCESS_TO_REPORTS,
  DEFAULT_ERROR_MESSAGE,
  DEFAULT_ERROR_TITLE,
  SORT_ASC_TITLE,
  SORT_DESC_TITLE,
  NO_TABLE_DATA_TITLE,
  ORDER_OFFER_TYPES,
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  LANGUAGE_TITLES,
  SUPPORTED_LOCALES,
  STATUS_CODES,
  ENCODING_OPTIONS,
  PDF_BACKGROUND_TYPE,
  PDF_PRESETS,
  CASE_STATUSES,
  PROJECT_STATUSES,
  REPORT_STATUSES,
  TICKET_STATUSES,
  ERROR_CODES,
  RESPONSE_MESSAGES,
  RESOURCES,
  RESOURCE_ACTIONS,
  DB_NAMING,
  SHIFT_LOG_STATUSES,
  USER_ACTIVITY_BASE_TYPES,
  USER_ACTIVITY_BASE_ENTITY,
  SPEECH_LANGUAGE,
  ENTRY_TYPES_FILES,
  ENTRY_TYPES_TEMPLATE_FILES,
  ENTRY_TYPES_DRAW,
  ROLES_NAMES,
  ENTRY_TYPES_DYNAMIC_SIZE_IN_PDF,
  DEFAULT_MASS_CORRECTION_FACTOR,
  ROLES_PRIORITY,
  ENTRY_TYPES_FILES_BELONG_MILESTONE,
  PROJECT_PDF_TYPES,
  ENTRY_TYPES_FILES_BELONG_TICKET,
  TICKET_TEMPLATE_DETAILS_REQUIRED_FIELDS,
  REPORT_TEMPLATE_DETAILS_REQUIRED_FIELDS,
  ONE_TEMPLATE_DETAILS_REQUIRED_FIELDS,
  NO_EDIT_BY_MOBILE,
  DOCX_CONVERT_ERROR,
  SIZE_IN_PDF_50,
  SIZE_IN_PDF_100,
  SIZE_IN_PDF,
  TYPES_LIST_WITHOUT_CHECKBOX,
  TYPES_LIST_THAT_COMPARE_WITH_OTHER,
  TYPES_OF_COMPARE,
  STICKER_TYPES,
  DYNAMIC_DATA_TYPES,
  TYPES_OF_FILLING_THE_FIELD,
  EMPTY_FIELD,
  FILLED_FIELD,
};
